import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import comptabiliteService, { LigneTiers, BalanceAgee, SuggestionLettrage } from '../../api/comptabilite';
import { formatTND } from '../../utils/formatters';

const today = () => new Date().toISOString().slice(0, 10);

const Lettrage: React.FC = () => {
  const [tab, setTab] = useState<'lettrage' | 'agee'>('lettrage');

  // --- Lettrage ---
  const [compte, setCompte] = useState('411');
  const [ouvertes, setOuvertes] = useState(true);
  const [lignes, setLignes] = useState<LigneTiers[]>([]);
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [suggestions, setSuggestions] = useState<SuggestionLettrage[]>([]);

  // --- Balance âgée ---
  const [type, setType] = useState<'CLIENT' | 'FOURNISSEUR'>('CLIENT');
  const [dateRef, setDateRef] = useState(today());
  const [agee, setAgee] = useState<BalanceAgee | null>(null);

  const chargerLignes = async () => {
    try {
      setLignes(await comptabiliteService.lignesTiers(compte, ouvertes));
      setSelection(new Set());
    } catch (e: any) {
      if (e?.response?.status !== 403) toast.error('Chargement impossible.');
    }
  };

  const chargerAgee = async () => {
    try {
      setAgee(await comptabiliteService.balanceAgee(type, dateRef));
    } catch (e: any) {
      if (e?.response?.status !== 403) toast.error('Chargement impossible.');
    }
  };

  useEffect(() => { if (tab === 'lettrage') chargerLignes(); /* eslint-disable-next-line */ }, [tab, compte, ouvertes]);
  useEffect(() => { if (tab === 'agee') chargerAgee(); /* eslint-disable-next-line */ }, [tab, type, dateRef]);

  const toggle = (id: number) => {
    const next = new Set(selection);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelection(next);
  };

  const soldeSelection = lignes
    .filter((l) => selection.has(l.id))
    .reduce((s, l) => s + Number(l.debit) - Number(l.credit), 0);

  const lettrer = async () => {
    try {
      const res = await comptabiliteService.lettrer(Array.from(selection));
      toast.success(`Lettré « ${res.code} » (${res.nbLignes} lignes).`);
      chargerLignes();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Lettrage impossible.');
    }
  };

  const delettrer = async (code: string) => {
    try {
      await comptabiliteService.delettrer(code);
      toast.success(`Délettré « ${code} ».`);
      chargerLignes();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Délettrage impossible.');
    }
  };

  // TF-060 — lettrage automatique : propositions débit/crédit de même tiers et montant.
  const suggerer = async () => {
    try {
      const res = await comptabiliteService.suggestionsLettrage(compte);
      setSuggestions(res);
      if (res.length === 0) toast('Aucune suggestion de lettrage.');
    } catch (e: any) {
      if (e?.response?.status !== 403) toast.error('Suggestions indisponibles.');
    }
  };

  const appliquerSuggestion = async (s: SuggestionLettrage) => {
    try {
      const res = await comptabiliteService.lettrer(s.lignes.map((l) => l.id));
      toast.success(`Lettré « ${res.code} ».`);
      setSuggestions((prev) => prev.filter((x) => x !== s));
      chargerLignes();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Lettrage impossible.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/comptabilite" className="text-sm text-blue-600 hover:underline">← Comptabilité</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Lettrage des tiers</h1>
        <p className="text-sm text-gray-500">Rapprochez factures et règlements ; suivez les créances/dettes par ancienneté.</p>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab('lettrage')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === 'lettrage' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200'}`}>
          Lettrage
        </button>
        <button onClick={() => setTab('agee')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === 'agee' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200'}`}>
          Balance âgée
        </button>
      </div>

      {tab === 'lettrage' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-4 bg-white p-4 rounded-xl border border-gray-200">
            <div>
              <label className="block text-sm font-medium mb-1">Compte (préfixe)</label>
              <input value={compte} onChange={(e) => setCompte(e.target.value)}
                     className="rounded-lg border border-gray-200 px-3 py-2 w-32" placeholder="411" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={ouvertes} onChange={(e) => setOuvertes(e.target.checked)} />
              Non lettrées seulement
            </label>
            <div className="ml-auto flex items-center gap-3">
              <span className={`text-sm ${Math.abs(soldeSelection) < 0.001 ? 'text-green-600' : 'text-gray-500'}`}>
                Solde sélection : {formatTND(soldeSelection)}
              </span>
              <button onClick={suggerer}
                      className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-50">
                Suggérer
              </button>
              <button onClick={lettrer} disabled={selection.size < 2}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
                Lettrer ({selection.size})
              </button>
            </div>
          </div>

          {suggestions.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
              <p className="text-sm font-semibold text-blue-900">
                Suggestions de lettrage automatique ({suggestions.length})
              </p>
              {suggestions.map((s, i) => (
                <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-sm">
                  <span className="text-gray-700">
                    <span className="font-medium">{s.tiersNom}</span> — {formatTND(s.montant)}
                    <span className="text-gray-400"> ({s.lignes.map((l) => l.ecritureNumero).join(' ↔ ')})</span>
                  </span>
                  <button onClick={() => appliquerSuggestion(s)}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Appliquer
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-3 py-3 w-10"></th>
                  <th className="text-left px-3 py-3">Date</th>
                  <th className="text-left px-3 py-3">Écriture</th>
                  <th className="text-left px-3 py-3">Compte</th>
                  <th className="text-left px-3 py-3">Libellé</th>
                  <th className="text-right px-3 py-3">Débit</th>
                  <th className="text-right px-3 py-3">Crédit</th>
                  <th className="text-center px-3 py-3">Lettrage</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((l) => (
                  <tr key={l.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={selection.has(l.id)} onChange={() => toggle(l.id)} />
                    </td>
                    <td className="px-3 py-2 text-gray-600">{l.date}</td>
                    <td className="px-3 py-2 text-gray-600">{l.ecritureNumero}</td>
                    <td className="px-3 py-2 text-gray-600">{l.compteNumero}</td>
                    <td className="px-3 py-2 text-gray-900">{l.libelle}</td>
                    <td className="px-3 py-2 text-right">{Number(l.debit) ? formatTND(l.debit) : ''}</td>
                    <td className="px-3 py-2 text-right">{Number(l.credit) ? formatTND(l.credit) : ''}</td>
                    <td className="px-3 py-2 text-center">
                      {l.lettrage ? (
                        <button onClick={() => delettrer(l.lettrage!)}
                                className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs font-semibold hover:bg-green-200">
                          {l.lettrage} ✕
                        </button>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                ))}
                {lignes.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Aucune ligne.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'agee' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-4 bg-white p-4 rounded-xl border border-gray-200">
            <div>
              <label className="block text-sm font-medium mb-1">Tiers</label>
              <select value={type} onChange={(e) => setType(e.target.value as any)}
                      className="rounded-lg border border-gray-200 px-3 py-2">
                <option value="CLIENT">Clients</option>
                <option value="FOURNISSEUR">Fournisseurs</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">À la date</label>
              <input type="date" value={dateRef} onChange={(e) => setDateRef(e.target.value)}
                     className="rounded-lg border border-gray-200 px-3 py-2" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-3 py-3">Tiers</th>
                  <th className="text-right px-3 py-3">Solde</th>
                  <th className="text-right px-3 py-3">Non échu</th>
                  <th className="text-right px-3 py-3">0–30 j</th>
                  <th className="text-right px-3 py-3">31–60 j</th>
                  <th className="text-right px-3 py-3">61–90 j</th>
                  <th className="text-right px-3 py-3">&gt; 90 j</th>
                </tr>
              </thead>
              <tbody>
                {agee?.lignes.map((l) => (
                  <tr key={l.tiersId} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-medium text-gray-900">{l.tiersNom}</td>
                    <td className="px-3 py-2 text-right font-semibold">{formatTND(l.solde)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{formatTND(l.nonEchu)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{formatTND(l.tranche30)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{formatTND(l.tranche60)}</td>
                    <td className="px-3 py-2 text-right text-amber-600">{formatTND(l.tranche90)}</td>
                    <td className="px-3 py-2 text-right text-red-600">{formatTND(l.plus90)}</td>
                  </tr>
                ))}
                {(!agee || agee.lignes.length === 0) && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Aucun solde ouvert.</td></tr>
                )}
              </tbody>
              {agee && agee.lignes.length > 0 && (
                <tfoot className="bg-gray-50 font-semibold">
                  <tr><td className="px-3 py-3">Total</td>
                    <td className="px-3 py-3 text-right">{formatTND(agee.totalSolde)}</td>
                    <td colSpan={5}></td></tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lettrage;
